import { useEffect } from 'react';
import { useFormik } from 'formik';
import { isEmpty } from 'lodash';
import {
  useNowEstablishPageTypeStore,
  useEstablishStore,
  useIsPrevPageConfirmStore,
  getBtnProps,
  TransitionBtn,
  useGetAddressInfo,
  reloadHandler,
} from 'views/components/pages/accountEstablishment/hooks';
import {
  EstablishPageType,
  costomerInfoOnSubmit,
  CostomerInfoFormikType,
  costomerInfoValidation,
  costomerInfoInitialValues,
  AccountEstablishRequestParam,
  formErrorScroll,
  scrollToTop,
} from 'views/utils/accountEstablish';
import { ButtonProps } from 'views/components/atoms/button';
import { useNavigate } from 'react-router-dom';

export const useCostomerInfo = () => {
  const navigate = useNavigate();
  const { nowEstablishPageType, setNowEstablishPageType } = useNowEstablishPageTypeStore();
  const { isPrevPageConfirm, setIsPrevPageConfirm } = useIsPrevPageConfirmStore();
  const { param, add } = useEstablishStore();
  const initialValues = costomerInfoInitialValues(param);
  const onSubmit = costomerInfoOnSubmit<AccountEstablishRequestParam>(param, add, () =>
    setNowEstablishPageType(isPrevPageConfirm ? EstablishPageType.RequestConfirm : EstablishPageType.DepositAccount),
  );

  const formik: CostomerInfoFormikType = useFormik({
    initialValues,
    validationSchema: costomerInfoValidation(false),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit,
  });

  const nextButtonProps: ButtonProps = getBtnProps(TransitionBtn.NEXT);
  const prevButtonProps: ButtonProps = getBtnProps(TransitionBtn.PREV, () =>
    setNowEstablishPageType(EstablishPageType.ConsentTrust),
  );
  const confirmButtonProps: ButtonProps = getBtnProps(TransitionBtn.CONFIRM);
  const searchButtonProps: ButtonProps = getBtnProps(TransitionBtn.SEARCH_ADDRESS, () => getAddressInfo());

  const { addressInfo, getAddressInfo } = useGetAddressInfo(formik.values.postCode);

  useEffect(() => {
    scrollToTop();
    window.addEventListener('beforeunload', reloadHandler);
    return () => {
      window.removeEventListener('beforeunload', reloadHandler);
    };
  }, []);

  useEffect(() => {
    if (nowEstablishPageType === EstablishPageType.CustomerInfo) return;
    navigate(nowEstablishPageType.toString());
  }, [nowEstablishPageType]);

  useEffect(() => {
    if (!addressInfo.data) return;
    formik.setFieldValue('prefecture', addressInfo.data.GetAddressInfo?.Prefecture);
    formik.setFieldValue('cityCounty', addressInfo.data.GetAddressInfo?.CityCounty ?? '');
    formik.setFieldValue('townStreetAddress', addressInfo.data.GetAddressInfo?.TownStreetAddress ?? '');
    formik.setFieldValue('isNoBuildingNameRoomNumber', false);
  }, [addressInfo]);

  useEffect(() => {
    if (isEmpty(formik.errors)) return;
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    formik,
    buttonProps: {
      nextButtonProps,
      prevButtonProps,
      confirmButtonProps,
      isPrevPageConfirm,
    },
    searchButtonProps,
    getAddressInfo,
    isUpdate: false,
  };
};
